import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createServiceAddon } from '../lib/services';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';

interface AddAddonModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

export default function AddAddonModal({
  isOpen,
  onClose,
  serviceId,
  onSuccess
}: AddAddonModalProps) {
  const { selectedBusiness } = useBusiness();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState<number>(0);
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!selectedBusiness) {
      toast.error('No business selected');
      return;
    }

    try {
      setSaving(true);
      await createServiceAddon({
        service_id: serviceId,
        business_id: selectedBusiness.id,
        name,
        description: description || undefined,
        price,
        duration_minutes: (hours * 60) + minutes,
        order_index: 0
      });

      toast.success('Add-on created successfully');
      onSuccess?.();
      onClose();
      
      // Reset form
      setName('');
      setDescription('');
      setPrice(0);
      setHours(0);
      setMinutes(0);
    } catch (err) {
      console.error('Error creating add-on:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isValid = name.trim() !== '' && price >= 0 && (hours > 0 || minutes > 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/30" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-md rounded-lg bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-lg font-medium">Add Service Add-on</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                placeholder="Enter add-on name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                  className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                  className="w-20 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
                <span className="text-sm text-gray-500">h</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                  className="w-20 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
                <span className="text-sm text-gray-500">m</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={!isValid || saving}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Add-on'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 